import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from 'api/client';
import { ENDPOINTS } from 'api/endpoints';
import {
  AttributeDefinition,
  CardDesign,
  OfferAppearance,
  ProductPageDesign,
  RelatedProductsConfig,
  StockAppearance,
} from 'types/product';
import { fromCardDesign } from 'utils/productPageDesign';
import { defaultOfferAppearance, normalizeOfferAppearance } from 'utils/offerAppearance';
import { defaultStockAppearance, normalizeStockAppearance } from 'utils/stockLevel';

interface CatalogPresentationValue {
  attributes: AttributeDefinition[];
  cardDesign: CardDesign | null;
  productPageDesign: ProductPageDesign | null;
  relatedProducts: RelatedProductsConfig;
  taxonomyLevels: string[];
  taxonomyLevelLabels: Record<string, string>;
  /** Falso: no se puede pedir más de lo disponible. */
  allowBackorders: boolean;
  stockAppearance: StockAppearance;
  offerAppearance: OfferAppearance;
  loading: boolean;
  reload: () => void;
}

const CatalogPresentationContext = createContext<CatalogPresentationValue | undefined>(undefined);

const defaultRelatedProducts = (): RelatedProductsConfig => ({
  enabled: true,
  title: 'Productos relacionados',
  limit: 8,
  matchSameLine: true,
  matchSameCategory: true,
  matchSameBrand: true,
  matchAttributes: true,
  fallbackSameLine: true,
  suggestWhenEmpty: true,
});

const defaultClassicDesign = (): CardDesign => ({
  template: 'classic',
  image: { aspect: '1/1', objectFit: 'contain', bg: 'muted' },
  style: { border: 'none', radius: 'rounded', shadow: 'soft', shadowWhen: 'always' },
  slots: [
    { id: 'overline', field: 'atributo1', visible: true },
    { id: 'title', field: 'name', visible: true },
    { id: 'meta', visible: true, fields: [{ field: 'sku', labelMode: 'none' }] },
    { id: 'price', field: 'price', visible: true },
    { id: 'badges', visible: false, flagFields: [] },
  ],
  actions: { primary: null, secondary: null, requireAuthForCart: true },
});

export const CatalogPresentationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [attributes, setAttributes] = useState<AttributeDefinition[]>([]);
  const [cardDesign, setCardDesign] = useState<CardDesign | null>(null);
  const [productPageDesign, setProductPageDesign] = useState<ProductPageDesign | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<RelatedProductsConfig>(defaultRelatedProducts);
  const [taxonomyLevels, setTaxonomyLevels] = useState<string[]>(['ramo']);
  const [taxonomyLevelLabels, setTaxonomyLevelLabels] = useState<Record<string, string>>({});
  const [allowBackorders, setAllowBackorders] = useState(true);
  const [stockAppearance, setStockAppearance] = useState<StockAppearance>(defaultStockAppearance);
  const [offerAppearance, setOfferAppearance] = useState<OfferAppearance>(defaultOfferAppearance);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{
        attributes?: AttributeDefinition[];
        cardDesign?: CardDesign;
        productPageDesign?: ProductPageDesign;
        relatedProducts?: RelatedProductsConfig;
        taxonomyLevels?: string[];
        taxonomyLevelLabels?: Record<string, string>;
        allowBackorders?: boolean;
        stockAppearance?: StockAppearance;
        offerAppearance?: OfferAppearance;
      }>(ENDPOINTS.CATALOG_CONFIG)
      .then(({ data }) => {
        if (cancelled) return;
        const nextCard = data.cardDesign ?? defaultClassicDesign();
        setAttributes(data.attributes ?? []);
        setCardDesign(nextCard);
        setProductPageDesign(data.productPageDesign ?? fromCardDesign(nextCard));
        setRelatedProducts({ ...defaultRelatedProducts(), ...data.relatedProducts });
        setTaxonomyLevels(
          Array.isArray(data.taxonomyLevels) && data.taxonomyLevels.length
            ? data.taxonomyLevels
            : ['ramo']
        );
        setTaxonomyLevelLabels(data.taxonomyLevelLabels ?? {});
        setAllowBackorders(data.allowBackorders ?? true);
        setStockAppearance(normalizeStockAppearance(data.stockAppearance));
        setOfferAppearance(normalizeOfferAppearance(data.offerAppearance));
      })
      .catch(() => {
        if (cancelled) return;
        const nextCard = defaultClassicDesign();
        setAttributes([]);
        setCardDesign(nextCard);
        setProductPageDesign(fromCardDesign(nextCard));
        setRelatedProducts(defaultRelatedProducts());
        setTaxonomyLevels(['ramo']);
        setTaxonomyLevelLabels({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const value = useMemo(
    () => ({
      attributes,
      cardDesign,
      productPageDesign,
      relatedProducts,
      taxonomyLevels,
      taxonomyLevelLabels,
      allowBackorders,
      stockAppearance,
      offerAppearance,
      loading,
      reload: () => setTick((n) => n + 1),
    }),
    [
      attributes,
      cardDesign,
      productPageDesign,
      relatedProducts,
      taxonomyLevels,
      taxonomyLevelLabels,
      allowBackorders,
      stockAppearance,
      offerAppearance,
      loading,
    ]
  );

  return (
    <CatalogPresentationContext.Provider value={value}>{children}</CatalogPresentationContext.Provider>
  );
};

export const useCatalogPresentation = () => {
  const ctx = useContext(CatalogPresentationContext);
  if (!ctx) throw new Error('useCatalogPresentation debe usarse dentro de CatalogPresentationProvider');
  return ctx;
};

export const useCatalogPresentationOptional = () => useContext(CatalogPresentationContext);
